import { useMutation } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo } from 'react';

import { retry } from '@/utils';
import { postTikTokEvent } from '@features/Analytics/api/tiktok';
import type { TpTikTokParameters } from '@features/Analytics/types';

const TIKTOK_CLICK_ID_KEY = 'ttclid';

const createEventId = (eventName: string) =>
  `${eventName}.${
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.floor(Math.random() * 100_000_000)
  }`;

const getCurrentUrl = () =>
  typeof window !== 'undefined' ? window.location.href : undefined;

const getReferrer = () =>
  typeof document !== 'undefined' ? document.referrer : undefined;

const getTikTokClickIdFromUrl = () => {
  if (typeof window === 'undefined') return undefined;

  const ttclid = new URLSearchParams(window.location.search).get('ttclid');

  if (ttclid) {
    sessionStorage.setItem(TIKTOK_CLICK_ID_KEY, ttclid);
    return ttclid;
  }

  return sessionStorage.getItem(TIKTOK_CLICK_ID_KEY) ?? undefined;
};

export const useTikTokEvent = () => {
  const { mutateAsync: postEvent } = useMutation({
    mutationFn: postTikTokEvent,
    mutationKey: ['post-tiktok-event'],
  });

  useEffect(() => {
    getTikTokClickIdFromUrl();
  }, []);

  const conditionCheckFunction = useCallback(
    async () =>
      // @ts-ignore
      window?.ttq != null,
    [],
  );

  const fetchClientEventWithRetry = useCallback(
    async ({
      eventName,
      options,
      customData,
      email,
      phone,
      externalId,
    }: TpTikTokParameters) => {
      const eventId = createEventId(eventName);
      const ttclid = getTikTokClickIdFromUrl();
      const ttp = Cookies.get('_ttp');

      const actionFunction = async () => {
        if (email) {
          // @ts-ignore
          window?.ttq?.identify({
            email,
          });
        }

        // @ts-ignore
        window?.ttq?.track(eventName, options, { event_id: eventId });
      };

      await retry({
        conditionCheckFunction,
        actionFunction,
        retryDelay: 300,
        maxRetries: 5,
      });

      await postEvent({
        eventName,
        eventId,
        options,
        customData,
        email,
        phone,
        externalId,
        ttclid,
        ttp,
        eventSourceUrl: getCurrentUrl(),
        referrer: getReferrer(),
      });
    },
    [conditionCheckFunction, postEvent],
  );

  return useMemo(
    () => ({
      clientEvent: (value: TpTikTokParameters) => {
        fetchClientEventWithRetry(value);
      },
    }),
    [fetchClientEventWithRetry],
  );
};
